// /shifts — Shift Management (PAGES.md §3.60)
//
// View all shift definitions across locations. Create, edit and deactivate
// shift slots. Uses the ShiftScheduler component from the design system for
// the visual weekly overview when data is available.
//
// NOTE: PAGES.md §3.60 describes a shift *record* view (cashier, balance,
// discrepancy). This page currently manages shift *definitions* (templates).
// Shift record history requires backend support not yet available.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Badge,
  Input,
  Skeleton,
  EmptyState,
  Modal,
  ConfirmDialog,
  StatCard,
  Tabs,
  useToast,
} from '@bbpos/ui';
import { ShiftReportsContent } from './ShiftReportsPage';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { locationService } from '@/services/location.service';
import {
  useShifts,
  useShiftStats,
  useCreateShift,
  useUpdateShift,
  useDeactivateShift,
  DaysPill,
  hhmm,
} from '@/features/shifts';
import type { Shift, CreateShiftInput } from '@/features/shifts';
import { workingSchedulesApi } from '@/features/working-schedules';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// 24-hour HH:mm, matching the backend @IsMilitaryTime() validator. Catches bad
// values (e.g. 25:99, 9am) inline before a pointless server round-trip (#171).
const TIME_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export function isValidShiftTime(value: string): boolean {
  return TIME_HHMM.test(value);
}

// An end before the start is an overnight shift (22:00–06:00) ending the next day.
export function isOvernightShift(start: string, end: string): boolean {
  return isValidShiftTime(start) && isValidShiftTime(end) && end < start;
}

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

type Row = Shift;

function ShiftManagementContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canManage = roleLevel >= ROLE_LEVELS.MANAGER;

  const page = Number(searchParams.get('page') ?? '1');
  const isActive = searchParams.get('is_active') ?? '';
  const search = searchParams.get('search') ?? '';

  const { data, isLoading, isFetching } = useShifts({
    page,
    is_active: isActive || undefined,
    search: search || undefined,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  // KPI totals come from a dataset-scoped stats endpoint that ignores the
  // is_active status filter and pagination, so "Active Shifts" / "Locations
  // Covered" describe the whole dataset rather than the current filtered page
  // (issue #170).
  const { data: stats } = useShiftStats({ search: search || undefined });

  const createMutation = useCreateShift();
  const updateMutation = useUpdateShift();
  const deactivateMutation = useDeactivateShift();

  // Searchable location picker — Owners/Managers should never need raw UUIDs.
  const { data: locationsData } = useQuery({
    queryKey: ['shift-locations-picker'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () =>
      (locationsData?.data ?? []).map((l) => ({
        value: l.id,
        label: l.location_code ? `${l.name} — ${l.location_code}` : l.name,
      })),
    [locationsData],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);

  const activeTab = (searchParams.get('tab') ?? 'templates') as 'templates' | 'reports';

  // Form state
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function openCreate() {
    setEditingShift(null);
    setName('');
    setLocationId('');
    setStartTime('09:00');
    setEndTime('17:00');
    setDaysOfWeek([1, 2, 3, 4, 5]);
    setModalOpen(true);
  }

  function openEdit(shift: Shift) {
    setEditingShift(shift);
    setName(shift.name);
    setLocationId(shift.location_id);
    // T23: a TIME may read back as HH:mm:ss, which the HH:mm check would refuse on save.
    setStartTime(hhmm(shift.start_time));
    setEndTime(hhmm(shift.end_time));
    setDaysOfWeek(shift.days_of_week);
    setModalOpen(true);
  }

  // T15: a new shift starts on the store's working week — its schedule, else its
  // country's (a Muscat store: Sun–Thu). Editing keeps the shift's own days.
  useEffect(() => {
    if (!modalOpen || editingShift || !locationId) return;
    let cancelled = false;
    workingSchedulesApi.effective(locationId)
      .then((eff) => { if (!cancelled && eff?.work_days?.length) setDaysOfWeek(eff.work_days); })
      .catch(() => { /* keep the current days — the API fills them in when omitted anyway */ });
    return () => { cancelled = true; };
  }, [modalOpen, editingShift, locationId]);

  function toggleDay(day: number) {
    setDaysOfWeek((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort());
  }

  // Keyboard: n → create, / → search
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'n' && canManage) { e.preventDefault(); openCreate(); }
      if (e.key === '/') {
        e.preventDefault();
        (document.getElementById('shift-search') as HTMLInputElement | null)?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canManage]);

  const handleSave = useCallback(() => {
    if (!name.trim()) { toast({ variant: 'warning', title: 'Name required' }); return; }
    if (!startTime || !endTime) { toast({ variant: 'warning', title: 'Times required' }); return; }
    if (!isValidShiftTime(startTime)) { toast({ variant: 'warning', title: 'Enter a valid start time', description: 'Use 24-hour HH:mm, e.g. 09:00' }); return; }
    if (!isValidShiftTime(endTime)) { toast({ variant: 'warning', title: 'Enter a valid end time', description: 'Use 24-hour HH:mm, e.g. 17:00' }); return; }
    if (startTime === endTime) { toast({ variant: 'warning', title: 'Start and end times must differ' }); return; }
    if (daysOfWeek.length === 0) { toast({ variant: 'warning', title: 'Select at least one day' }); return; }

    if (editingShift) {
      updateMutation.mutate(
        { id: editingShift.id, input: { name: name.trim(), start_time: startTime, end_time: endTime, days_of_week: daysOfWeek } },
        {
          onSuccess: () => { toast({ variant: 'success', title: 'Shift updated' }); setModalOpen(false); },
          onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
        },
      );
    } else {
      if (!locationId) { toast({ variant: 'warning', title: 'Location required' }); return; }
      const input: CreateShiftInput = { location_id: locationId, name: name.trim(), start_time: startTime, end_time: endTime, days_of_week: daysOfWeek };
      createMutation.mutate(input, {
        onSuccess: () => { toast({ variant: 'success', title: 'Shift created' }); setModalOpen(false); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      });
    }
  }, [editingShift, name, locationId, startTime, endTime, daysOfWeek, createMutation, updateMutation, toast]);

  const handleDeactivate = useCallback(() => {
    if (!confirmDeactivate) return;
    deactivateMutation.mutate(confirmDeactivate, {
      onSuccess: () => { toast({ variant: 'success', title: 'Shift deactivated' }); setConfirmDeactivate(null); },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
    });
  }, [confirmDeactivate, deactivateMutation, toast]);

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        key: 'name' as keyof Row,
        header: 'Shift Name',
        render: (_v: unknown, row: Row) =>
          canManage ? (
            <button type="button" onClick={() => openEdit(row)} className="group text-left -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
              <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">{row.name}</p>
            </button>
          ) : (
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.name}</p>
          ),
      },
      {
        key: 'location_id' as keyof Row,
        header: 'Location',
        render: (_v: unknown, row: Row) => (
          <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.location_name ?? row.location_id.slice(0, 8)}</span>
        ),
      },
      {
        key: 'start_time' as keyof Row,
        header: 'Start',
        render: (_v: unknown, row: Row) => <span className="text-sm tabular-nums font-mono">{hhmm(row.start_time)}</span>,
      },
      {
        key: 'end_time' as keyof Row,
        header: 'End',
        render: (_v: unknown, row: Row) => <span className="text-sm tabular-nums font-mono">{hhmm(row.end_time)}</span>,
      },
      {
        key: 'days_of_week' as keyof Row,
        header: 'Days',
        render: (_v: unknown, row: Row) => <DaysPill days={row.days_of_week} />,
      },
      {
        key: 'is_active' as keyof Row,
        header: 'Status',
        render: (_v: unknown, row: Row) => (
          <Badge variant={row.is_active ? 'success' : 'voided'} size="sm" dot>
            {row.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      ...(canManage ? [{
        key: 'actions' as keyof Row,
        header: '',
        render: (_v: unknown, row: Row) => row.is_active ? (
          <Button variant="secondary" onClick={() => setConfirmDeactivate(row.id)} className="text-xs px-2 py-1">Deactivate</Button>
        ) : null,
      }] : []),
    ],
    [canManage],
  );

  const totalCount = stats?.total ?? data?.meta.total ?? 0;
  const activeCount = stats?.active ?? 0;
  const locationCount = stats?.locations ?? 0;

  // KPI icons
  const totalIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const activeIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const locIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  );

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Scheduling
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Shift Management</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">Define shifts per location with operating hours and working days.</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {totalCount.toLocaleString('en-IN')} shifts
              </span>
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {locationCount} locations
              </span>
            </div>
          </div>
          {canManage ? (
            <Button variant="primary" onClick={openCreate} className="!bg-white !text-indigo-700 hover:!bg-white/90 !border-transparent">+ Create Shift</Button>
          ) : null}
        </div>
      </div>

      {/* Tabs — Templates (current) vs Reports (per-session billing) */}
      <div className="mb-6">
        <Tabs
          items={[
            { id: 'templates', label: 'Shift Templates' },
            { id: 'reports', label: 'Reports' },
          ]}
          activeId={activeTab}
          onChange={(id) => setParam('tab', String(id))}
          variant="pill"
        />
      </div>

      {activeTab === 'reports' ? (
        <ShiftReportsContent embedded />
      ) : (
      <>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {isLoading ? (
          <>
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
          </>
        ) : (
          <>
            <StatCard title="Total Shifts" value={String(totalCount)} icon={totalIcon} />
            <StatCard title="Active Shifts" value={String(activeCount)} icon={activeIcon} />
            <StatCard title="Locations Covered" value={String(locationCount)} icon={locIcon} />
          </>
        )}
      </div>

      {/* Filter bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex-1 min-w-0">
            <Input
              id="shift-search"
              variant="search"
              placeholder="Search by shift name or location (press / to focus)"
              value={search}
              onChange={(v) => setParam('search', v)}
              aria-label="Search shifts"
            />
          </div>
          <div className="w-full lg:w-44">
            <Select label="Status" placeholder="All" options={ACTIVE_OPTIONS} value={isActive} onChange={(v) => setParam('is_active', typeof v === 'string' ? v : '')} />
          </div>
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No shifts" description="Create your first shift definition to start scheduling staff."
            action={canManage ? { label: '+ Create Shift', onClick: openCreate } : undefined}
            icon={<svg className="h-20 w-20 text-indigo-300 dark:text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}
      </>
      )}

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingShift ? 'Edit Shift' : 'Create Shift'} variant="wide">
        <div className="space-y-4">
          <Input label="Shift Name" value={name} onChange={(v) => setName(v.slice(0, 100))} placeholder="e.g. Morning Shift" required />
          {!editingShift ? (
            <Select
              label="Location"
              variant="searchable"
              options={locationOptions}
              value={locationId}
              onChange={(v) => setLocationId(typeof v === 'string' ? v : '')}
              placeholder="Search location by name…"
              required
            />
          ) : null}
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Time (HH:mm)" value={startTime} onChange={setStartTime} placeholder="09:00" required />
            <Input label="End Time (HH:mm)" value={endTime} onChange={setEndTime} placeholder="17:00" required />
          </div>
          {isOvernightShift(startTime, endTime) ? (
            <p className="-mt-2 text-xs text-indigo-600 dark:text-indigo-400">
              Overnight shift — ends at {endTime} the next day. It counts under the day it starts.
            </p>
          ) : null}
          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Working Days</p>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((label, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(idx)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ring-1 ${
                    daysOfWeek.includes(idx)
                      ? 'bg-indigo-500 text-white ring-indigo-600 shadow-sm'
                      : 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-700 dark:text-neutral-200 ring-neutral-200/60 dark:ring-neutral-600/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending}>
              {editingShift ? 'Save Changes' : 'Create Shift'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(confirmDeactivate)} onCancel={() => setConfirmDeactivate(null)} onConfirm={handleDeactivate}
        title="Deactivate Shift" description="This shift will no longer appear in scheduling. Existing attendance records are not affected." variant="warning" loading={deactivateMutation.isPending} />
    </div>
  );
}

export function ShiftManagementPage() {
  return (
    <PageContainer title="Shifts" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Shifts' }]}>
      <ErrorBoundary resetKey="shift-management">
        <ShiftManagementContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
